import assetsTranslations from 'translations'
import i18n from '@/i18n'

export const AssetsTranslationMixin = {
    methods: {
        /**
         * Translates a key using the assets translations
         * @param {string} key The localize key to translate
         * @returns {string} The translated key or the key itself if no translation is found
         */
        $trans (key) {
            const locale = this.$i18n.locale // The locale here is the locale FROM the store-web-front i18n instance OR if not hosted on store-web-front, the locale from the common package i18n instance

            return translate(key, locale)
        }
    }
}

/**
 * Checks if the key is prefixed with a network (one or more numbers and a dot)
 * @param {string} key The localize key we want to check
 * @returns {boolean} True if the key is prefixed with a number and a dot, false otherwise
 */
const isNetworkPrefixedKey = (key) => /^\d+\./.test(key)

/**
 * Get the key without the network prefix
 * @param {string} key The localize key we want to strip the network prefix from
 * @returns {string} The key without network prefix
 */
const getKeyWithoutNetworkPrefix = (key) => key.replace(/^\d+\./, '')

const applyParamsToTranslation = (translation, params) => {
    for (const prop in params) {
        const pattern = `{${prop}}`
        translation = translation.replace(new RegExp(pattern, 'g'), params[prop])
    }

    return translation
}

const translate = (key, locale, params = {}) => {
    // We take the assets translations for the locale
    const language = assetsTranslations[locale]

    // If no translations are found for the locale, we log an error and return the key itself
    if (!language) {
        console.error(`No translations found for locale ${locale}`)
        return key
    }

    // If the key is prefixed with a network, we try to find the translation without the network prefix
    const defaultKey = isNetworkPrefixedKey(key) ? getKeyWithoutNetworkPrefix(key) : key

    // We try to return the translation for the key, if no translation is found, we return the translation for the key without the network prefix, if no translation is found, we return the key itself
    if (language[key] || language[key] === '') {
        return applyParamsToTranslation(language[key], params)
    }

    if (language[defaultKey]) {
        return applyParamsToTranslation(language[defaultKey], params)
    }

    return key
}

/**
 * Creates a translator function that prefixes the key with the network id
 * @param {string} network The network id to prefix all keys with
 * @returns {function(*, *): *} The translated key or the key itself if no translation is found
 */
export const createTranslatorWithDefaultNetworkWithI18nImported = (network = 'default') => {
    return (key, params) => {
        const locale = i18n.global.locale // The locale here is the locale FROM the store-web-front i18n instance OR if not hosted on store-web-front, the locale from the common package i18n instance

        const prefixedKey = network === 'default' ? key : `${network}.${key}`

        return translate(prefixedKey, locale, params)
    }
}

/**
 * Creates a translator function that prefixes the key with the network id
 * @param {string} network The network id to prefix all keys with
 * @returns {function(*, *): *} The translated key or the key itself if no translation is found
 */
export const createTranslatorWithDefaultNetwork = (network) => {
    return function (key, params) {
        const locale = i18n.global.locale // The locale here is the locale FROM the store-web-front i18n instance OR if not hosted on store-web-front, the locale from the common package i18n instance

        const prefixedKey = `${network}.${key}`

        return translate(prefixedKey, locale, params)
    }
}
